import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LegalHRPDFExport from '../../components/legal-hr/LegalHRPDFExport';
import toast from 'react-hot-toast';
import { anthropicService } from '../../services/anthropicService';
import { supabase } from '../../lib/supabase';
import { legalHrStorageService, SavedLegalHrDocument } from '../../services/legalHrStorageService';

// Define supported countries for terms and conditions
const supportedCountries = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'EU', name: 'European Union', flag: '🇪🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

// Define business types for dropdown
const businessTypes = [
  'E-commerce Store',
  'SaaS Application',
  'Mobile App',
  'Blog/Content Website',
  'Personal Portfolio',
  'Corporate Website',
  'Educational Platform',
  'Non-profit Organization',
  'Online Marketplace',
  'Social Media Platform',
];

// Define terms sections
const termsOptions = [
  { id: 'intellectual_property', label: 'Intellectual Property', example: 'Copyright, trademarks, and content ownership' },
  { id: 'user_accounts', label: 'User Accounts', example: 'Account creation, termination, and security responsibilities' },
  { id: 'acceptable_use', label: 'Acceptable Use Policy', example: 'Prohibited activities and content restrictions' },
  { id: 'payment_terms', label: 'Payment Terms', example: 'Pricing, billing cycles, refunds and cancellations' },
  { id: 'liability', label: 'Limitation of Liability', example: 'Disclaimers and liability limitations' },
  { id: 'dispute_resolution', label: 'Dispute Resolution', example: 'Arbitration, jurisdiction, and governing law' },
  { id: 'termination', label: 'Termination', example: 'Account suspension and termination conditions' },
];

const TermsConditionsGenerator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [termsGenerated, setTermsGenerated] = useState(false);
  
  // Form state
  const [businessName, setBusinessName] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('GB');
  const [selectedTerms, setSelectedTerms] = useState<string[]>([
    'intellectual_property',
    'acceptable_use',
    'liability'
  ]);
  const [subscriptionService, setSubscriptionService] = useState(false);
  const [userAccounts, setUserAccounts] = useState(false);
  const [termsText, setTermsText] = useState('');
  const [latestDocument, setLatestDocument] = useState<SavedLegalHrDocument | null>(null);
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const handleTermsOptionChange = (termId: string) => {
    if (selectedTerms.includes(termId)) {
      setSelectedTerms(selectedTerms.filter(item => item !== termId));
    } else {
      setSelectedTerms([...selectedTerms, termId]);
    }
  };

  const generateTerms = async () => {
    // Validation
    if (!businessName) {
      toast.error('Please enter your business name');
      return;
    }
    
    if (!businessWebsite) {
      toast.error('Please enter your business website');
      return;
    }
    
    if (!businessType) {
      toast.error('Please select your business type');
      return;
    }
    
    if (!contactEmail) {
      toast.error('Please enter a contact email');
      return;
    }
    
    if (selectedTerms.length === 0) {
      toast.error('Please select at least one terms section');
      return;
    }

    setGenerating(true);
    
    try {
      // Get the country name from the selected country code
      const selectedCountryData = supportedCountries.find(country => country.code === selectedCountry);
      const countryName = selectedCountryData ? selectedCountryData.name : 'United Kingdom';
      
      // Get current user ID from Supabase
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Format the selected terms sections into a readable list
      const selectedTermsSections = termsOptions
        .filter(term => selectedTerms.includes(term.id))
        .map(term => `${term.label}`)
        .join(', ');

      // Create a comprehensive prompt for Anthropic
      const prompt = `
Generate comprehensive, professional Terms and Conditions for a ${businessType} called "${businessName}" (${businessWebsite}).

The business is primarily operating in ${countryName} and these terms must comply with all relevant laws and regulations in that jurisdiction.

The terms should include the following sections: ${selectedTermsSections}${userAccounts ? ', User Accounts' : ''}${subscriptionService ? ', Payment Terms including subscription service details' : ''}.

Contact email for legal inquiries: ${contactEmail}
Effective date for the terms: ${effectiveDate}

Please format the terms and conditions in markdown, with appropriate sections, headings, and comprehensive coverage of all required legal elements for ${countryName}.

Make sure to include specific legal requirements for ${countryName} such as ${selectedCountry === 'EU' ? 'EU consumer protection laws, GDPR references where relevant' : selectedCountry === 'US' ? 'applicable US state and federal laws' : selectedCountry === 'CA' ? 'Canadian consumer protection laws' : selectedCountry === 'GB' ? 'UK consumer laws and regulations' : 'applicable local regulations'}.

Include proper clauses for dispute resolution based on ${countryName} jurisdiction.
`;

      // Make the actual API call to Anthropic through our service
      const response = await anthropicService.generateText(prompt);
      
      // Use the generated terms text from the API
      const generatedTermsText = `
# Terms and Conditions for ${businessName}

## Effective Date: ${effectiveDate}

Please read these Terms and Conditions ("Terms", "Terms and Conditions") carefully before using the ${businessWebsite} website (the "Service") operated by ${businessName} ("us", "we", or "our").

Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users and others who access or use the Service.

**By accessing or using the Service you agree to be bound by these Terms. If you disagree with any part of the terms then you may not access the Service.**

${selectedTerms.includes('intellectual_property') ? `
## Intellectual Property

The Service and its original content, features, and functionality are and will remain the exclusive property of ${businessName} and its licensors. The Service is protected by copyright, trademark, and other laws of both the ${selectedCountry === 'GB' ? 'United Kingdom' : selectedCountry === 'US' ? 'United States' : selectedCountry === 'EU' ? 'European Union' : selectedCountry === 'CA' ? 'Canada' : selectedCountry === 'AU' ? 'Australia' : 'New Zealand'} and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of ${businessName}.

Content that you submit, post, or display on or through the Service is your responsibility. You retain any intellectual property rights to content you create and submit to the Service.
` : ''}

${userAccounts ? `
## User Accounts

When you create an account with us, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.

You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password, whether your password is with our Service or a third-party service.

You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
` : ''}

${selectedTerms.includes('acceptable_use') ? `
## Acceptable Use

You agree not to use the Service:

* In any way that violates any applicable national or international law or regulation.
* To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail", "chain letter," "spam," or any other similar solicitation.
* To impersonate or attempt to impersonate ${businessName}, a ${businessName} employee, another user, or any other person or entity.
* In any way that infringes upon the rights of others, or in any way is illegal, threatening, fraudulent, or harmful.
* To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which may harm ${businessName} or users of the Service.
` : ''}

${subscriptionService ? `
## Payment Terms

### Subscription Service
${businessName} offers subscription-based services. By subscribing to our services, you agree to the following terms:

* Subscription fees are charged in advance on a recurring basis according to the billing cycle you select.
* You authorize us to charge the payment method you provide to us for all applicable fees.
* Subscriptions automatically renew unless canceled by you at least [X] days before the end of the current billing period.
* Prices for our services may change. We will provide notice of any price changes at least [X] days before they take effect.

### Refunds
Our refund policy is as follows:
* Refunds are issued at our discretion.
* To request a refund, please contact us at ${contactEmail}.
` : ''}

${selectedTerms.includes('liability') ? `
## Limitation of Liability

In no event shall ${businessName}, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:

* Your access to or use of or inability to access or use the Service;
* Any conduct or content of any third party on the Service;
* Any content obtained from the Service; and
* Unauthorized access, use or alteration of your transmissions or content.

This limitation applies whether the alleged liability is based on contract, tort, negligence, strict liability, or any other basis, even if ${businessName} has been advised of the possibility of such damage.
` : ''}

${selectedTerms.includes('dispute_resolution') ? `
## Governing Law and Dispute Resolution

These Terms shall be governed and construed in accordance with the laws of ${selectedCountry === 'GB' ? 'England and Wales' : selectedCountry === 'US' ? 'the United States' : selectedCountry === 'EU' ? 'the European Union' : selectedCountry === 'CA' ? 'Canada' : selectedCountry === 'AU' ? 'Australia' : 'New Zealand'}, without regard to its conflict of law provisions.

Any dispute arising out of or in connection with these Terms, including any question regarding its existence, validity or termination, shall be referred to and finally resolved by ${selectedCountry === 'US' ? 'arbitration under the rules of the American Arbitration Association' : 'the competent courts of ' + (selectedCountry === 'GB' ? 'England and Wales' : selectedCountry === 'EU' ? 'the country of your residence within the EU' : selectedCountry === 'CA' ? 'the Province of Ontario' : selectedCountry === 'AU' ? 'New South Wales' : 'New Zealand')}.
` : ''}

${selectedTerms.includes('termination') ? `
## Termination

We may terminate or suspend access to our Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.

All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity and limitations of liability.

We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
` : ''}

## Changes to These Terms

We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.

By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.

## Contact Us

If you have any questions about these Terms, please contact us at ${contactEmail}.
      `;
      
      setTermsText(response);
      setLatestDocument(null);
      setTermsGenerated(true);
      toast.success('Terms & Conditions generated successfully!');
    } catch (error) {
      console.error('Error generating terms and conditions:', error);
      toast.error('Failed to generate terms and conditions. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (termsText) {
      navigator.clipboard.writeText(termsText)
        .then(() => toast.success('Terms and Conditions copied to clipboard'))
        .catch((err) => {
          console.error('Failed to copy:', err);
          toast.error('Failed to copy text');
        });
    } else {
      toast.error('No content to copy');
    }
  };

  const handlePdfExportComplete = async ({ blob }: { filename: string; blob: Blob }) => {
    if (!user?.id) {
      toast.error('You must be logged in to save the terms');
      return;
    }

    try {
      const saved = await legalHrStorageService.saveLegalHrDocument({
        userId: user.id,
        docType: 'terms-conditions',
        blob,
        baseName: businessName || businessWebsite || 'terms-and-conditions',
        effectiveDate,
        metadata: {
          businessName,
          businessWebsite,
          businessType,
          contactEmail,
          jurisdiction: selectedCountry,
          selectedTerms,
          subscriptionService,
          userAccounts,
        },
        tags: [
          `jurisdiction:${selectedCountry}`,
          subscriptionService ? 'subscription-service:yes' : 'subscription-service:no',
          userAccounts ? 'user-accounts:enabled' : 'user-accounts:disabled',
          `sections:${selectedTerms.length}`,
        ],
      });

      setLatestDocument(saved);
      toast.success(`Terms & Conditions saved to workspace as version v${saved.version}`);
    } catch (error) {
      console.error('Failed to upload Terms & Conditions PDF', error);
      toast.error('Failed to save Terms & Conditions to storage');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with gradient */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
            Terms & Conditions Generator
          </h1>
          <p className="mt-3 text-xl text-gray-600 max-w-2xl mx-auto">
            Create customized terms and conditions for your website or application with jurisdiction-specific clauses.
          </p>
        </div>

        {/* Main content */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {!termsGenerated ? (
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column - Basic Info */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Business Information</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name*
                      </label>
                      <input
                        type="text"
                        id="businessName"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your Company Ltd"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="businessWebsite" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Website*
                      </label>
                      <input
                        type="text"
                        id="businessWebsite"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com"
                        value={businessWebsite}
                        onChange={(e) => setBusinessWebsite(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Email*
                      </label>
                      <input
                        type="email"
                        id="contactEmail"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="legal@example.com"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Type*
                      </label>
                      <select
                        id="businessType"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                      >
                        <option value="">Select a business type</option>
                        {businessTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                        Primary Jurisdiction*
                      </label>
                      <select
                        id="country"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                      >
                        {supportedCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Effective Date
                      </label>
                      <input
                        type="date"
                        id="effectiveDate"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Right column - Terms Options */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Terms Options</h2>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      What sections should be included in your terms?*
                    </label>
                    <div className="space-y-3">
                      {termsOptions.map((term) => (
                        <div key={term.id} className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id={term.id}
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              checked={selectedTerms.includes(term.id)}
                              onChange={() => handleTermsOptionChange(term.id)}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor={term.id} className="font-medium text-gray-700">
                              {term.label}
                            </label>
                            <p className="text-gray-500">{term.example}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-6 space-y-4">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="userAccounts"
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          checked={userAccounts}
                          onChange={() => setUserAccounts(!userAccounts)}
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="userAccounts" className="font-medium text-gray-700">
                          Users can create accounts
                        </label>
                        <p className="text-gray-500">Include terms related to user accounts, responsibilities, and security</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="subscriptionService"
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          checked={subscriptionService}
                          onChange={() => setSubscriptionService(!subscriptionService)}
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="subscriptionService" className="font-medium text-gray-700">
                          Subscription-based service
                        </label>
                        <p className="text-gray-500">Include terms related to subscriptions, billing, and cancellations</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Generate button */}
              <div className="mt-8 flex justify-center">
                <button
                  onClick={generateTerms}
                  disabled={generating}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    'Generate Terms & Conditions'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <div className="mb-6 flex justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Generated Terms & Conditions</h2>
                <div className="space-x-2">
                  <button
                    onClick={handleCopyToClipboard}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Copy to Clipboard
                  </button>
                  {termsText && (
                    <LegalHRPDFExport
                      contentRef={contentRef}
                      filename="terms-and-conditions.pdf"
                      title={businessName ? `${businessName} - Terms & Conditions` : 'Terms & Conditions'}
                      documentType="Terms & Conditions"
                      onExportComplete={handlePdfExportComplete}
                    />
                  )}
                  <button
                    onClick={() => setTermsGenerated(false)}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Edit
                  </button>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md" ref={contentRef}>
                <div dangerouslySetInnerHTML={{ __html: termsText }} />
              </div>

              <div className="mt-8 text-center">
                {latestDocument?.signedUrl && (
                  <div className="mb-4">
                    <a
                      href={latestDocument.signedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      View saved Terms & Conditions (v{latestDocument.version})
                    </a>
                  </div>
                )}
                <div className="bg-yellow-50 p-4 border border-yellow-100 rounded-md mb-4">
                  <p className="text-sm text-yellow-700">
                    <strong>Legal Disclaimer:</strong> These terms and conditions are generated by AI and should be reviewed by a qualified legal professional before use. Laws and regulations vary by jurisdiction and may change over time. The generated document may not cover all legal requirements specific to your business or jurisdiction.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setTermsGenerated(false);
                    window.scrollTo(0, 0);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Generate Another Document
                </button>
              </div>
            </div>
          )}
          
          {/* Disclaimer */}
          <div className="bg-yellow-50 p-4 border-t border-yellow-100">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Legal Disclaimer</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This generator provides a template only and is not a substitute for professional legal advice. 
                    The generated terms should be reviewed by a qualified legal professional before implementation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsGenerator;
